/**
 * deriveKitRowCalc
 *
 * Attaches a Valtio subscription to a kit-header row that scales member-row
 * cell values when the header's driver cell changes.
 *
 * Vars exposed to each `KitRowColumnCalc.formula`:
 *   - `member`     — the member row's current numeric cell value
 *   - `headerPrev` — the kit header's value before this change (module cache)
 *   - `headerNext` — the kit header's value after this change (current proxy)
 *
 * Calls only fire on header driver-cell change. The first attach seeds
 * `headerPrev` from the current value, so the FIRST user edit triggers
 * scaling. Locked and `suppressFormulas` members are skipped.
 *
 * `headerPrev` cache is module-scoped, not persisted; drift across repeated
 * edits is accepted (per plan SYD-2102).
 */

import { subscribe } from 'valtio';
import debounce from 'lodash/debounce';
import { evaluateFormula, parseFormula } from '#types';
import type {
  ArRowData,
  ArRowId,
  KitRowColumnCalc,
  TypeId,
} from '#types';
import type { CollectionPersist, CollectionState, DocEntry } from '../Store.types';

const kitRowCalcDerivationKey = Symbol('arTable.kitRowCalcDerivation');
const calcDebounceMs = 16;

type RowEntryProxy = DocEntry<ArRowData> & Record<string | symbol, unknown>;

const headerPrevCache = new Map<string, number>();

const cacheKey = (rowId: ArRowId, typeId: TypeId): string => `${rowId}|${typeId}`;

function readNumericCell(rowData: ArRowData | undefined, typeId: TypeId): number | undefined {
  const value = rowData?.cells?.[typeId]?.value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function deriveKitRowCalc(
  headerRowId: ArRowId,
  headerRowEntry: DocEntry<ArRowData>,
  calcs: KitRowColumnCalc[],
  rowsState: CollectionState<ArRowData>,
  persist: CollectionPersist<ArRowData>,
): () => void {
  const proxy = headerRowEntry as RowEntryProxy;
  const existing = proxy[kitRowCalcDerivationKey] as { cleanup: () => void } | undefined;
  if (existing) return existing.cleanup;

  if (calcs.length === 0) return () => undefined;

  for (const { formula } of calcs) {
    try { parseFormula(formula); } catch (err) {
      console.error('[deriveKitRowCalc] Failed to parse formula:', formula, err);
    }
  }

  for (const { typeId } of calcs) {
    const seed = readNumericCell(headerRowEntry.data, typeId);
    if (seed !== undefined) headerPrevCache.set(cacheKey(headerRowId, typeId), seed);
  }

  let isApplying = false;

  const compute = (): void => {
    if (isApplying) return;
    isApplying = true;
    try {
      const headerData = headerRowEntry.data;
      if (!headerData) return;

      for (const { typeId, formula } of calcs) {
        const headerNext = readNumericCell(headerData, typeId);
        if (headerNext === undefined) continue;

        const key = cacheKey(headerRowId, typeId);
        const headerPrev = headerPrevCache.get(key);
        if (headerPrev === undefined) {
          headerPrevCache.set(key, headerNext);
          continue;
        }
        if (headerPrev === headerNext) continue;

        for (const [memberRowId, memberEntry] of rowsState.docs) {
          if (memberRowId === headerRowId) continue;
          const memberData = memberEntry?.data;
          if (!memberData) continue;
          if (memberData.bundleId !== headerRowId) continue;
          if (memberData.bundleRole !== 'member') continue;
          if (memberData.locked === true) continue;
          if (memberData.suppressFormulas === true) continue;

          const member = readNumericCell(memberData, typeId);
          if (member === undefined) continue;

          const result = evaluateFormula(formula, (varName: string) => {
            if (varName === 'member') return member;
            if (varName === 'headerPrev') return headerPrev;
            if (varName === 'headerNext') return headerNext;
            return 0;
          });
          if (typeof result !== 'number' || !Number.isFinite(result)) continue;

          const mutableData = memberData as Record<string, unknown>;
          if (!mutableData.cells || typeof mutableData.cells !== 'object') {
            mutableData.cells = {};
          }
          const cells = mutableData.cells as Record<string, Record<string, unknown>>;
          if (!cells[typeId] || typeof cells[typeId] !== 'object') {
            cells[typeId] = { value: null };
          }
          if (Object.is(cells[typeId].value, result)) continue;

          cells[typeId].value = result;
          persist.update(memberRowId, { [`cells.${typeId}.value`]: result }).catch((err) => {
            console.error('[deriveKitRowCalc] Failed to persist member value:', err);
          });
        }

        headerPrevCache.set(key, headerNext);
      }
    } finally {
      isApplying = false;
    }
  };

  const debouncedCompute = debounce(compute, calcDebounceMs, { leading: false, trailing: true });

  const unsubscribe = subscribe(headerRowEntry.data, () => {
    if (isApplying) return;
    debouncedCompute();
  });

  const cleanup = (): void => {
    unsubscribe();
    debouncedCompute.cancel();
    delete proxy[kitRowCalcDerivationKey];
    for (const { typeId } of calcs) {
      headerPrevCache.delete(cacheKey(headerRowId, typeId));
    }
  };

  proxy[kitRowCalcDerivationKey] = { cleanup };
  return cleanup;
}

export default deriveKitRowCalc;
